/**
 * Normaliza URLs de medios (Cloudinary, Bunny, pull zones, etc.).
 * - Si falta el esquema (ej. jamrock.b-cdn.net/products/x.webp), antepone https://
 *   para que el navegador no la resuelva como ruta relativa.
 * - Recorta basura antes de http(s) y caracteres inválidos al final.
 */
export function cleanMediaUrl(url) {
  if (url == null || url === '') return '';
  let s = String(url).trim().replace(/^['"]|['"]$/g, '');
  if (!s) return '';

  if (s.startsWith('//')) {
    s = `https:${s}`;
  } else if (!/^https?:\/\//i.test(s)) {
    // dominio.tld/... sin https:// (típico si BUNNY_CDN_BASE_URL se guardó sin esquema)
    const looksLikeHostPath = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(:\d+)?(\/|$)/i;
    if (looksLikeHostPath.test(s)) {
      s = `https://${s.replace(/^\/+/, '')}`;
    }
  }

  const httpIndex = s.indexOf('http');
  if (httpIndex > 0) {
    s = s.slice(httpIndex);
  }
  const m = s.match(/^https?:\/\/[^\s"'<>]+/i);
  return m ? m[0] : s;
}

/** @deprecated Usar cleanMediaUrl; se mantiene por compatibilidad con imports existentes. */
export const cleanCloudinaryUrl = cleanMediaUrl;

/**
 * Normaliza un texto para búsqueda eliminando caracteres especiales,
 * acentos, guiones y espacios extras.
 * Ejemplo: "Clipper - Strangers" -> "clipper strangers"
 * @param {string} text - Texto a normalizar
 * @returns {string} - Texto normalizado
 */
export function normalizeSearchText(text) {
  if (!text) return '';
  
  return text
    .toLowerCase()
    // Normalizar caracteres acentuados (NFD separa el caracter base del acento)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos/acentos
    // Reemplazar guiones, barras y caracteres especiales comunes por espacio
    .replace(/[-_/\\|]+/g, ' ')
    // Eliminar caracteres no alfanuméricos excepto espacios (más permisivo)
    .replace(/[^a-z0-9\s]/g, '')
    // Colapsar múltiples espacios en uno solo
    .replace(/\s+/g, ' ')
    // Eliminar espacios al inicio y final
    .trim();
}

/**
 * Verifica si un texto contiene todos los términos de búsqueda.
 * Realiza búsqueda fuzzy dividiendo el query en palabras.
 * @param {string} text - Texto donde buscar
 * @param {string} query - Query de búsqueda
 * @returns {boolean} - True si el texto contiene todos los términos
 */
export function matchesSearch(text, query) {
  if (!query || !text) return !query; // Si no hay query, todo coincide
  
  const normalizedText = normalizeSearchText(text);
  const normalizedQuery = normalizeSearchText(query);
  
  // Si el query normalizado está vacío, no hay coincidencia
  if (!normalizedQuery) return false;
  
  // Dividir el query en palabras individuales
  const queryTerms = normalizedQuery.split(' ').filter(term => term.length > 0);
  
  // Si no hay términos, no hay coincidencia
  if (queryTerms.length === 0) return false;
  
  // Verificar que todos los términos estén presentes en el texto
  return queryTerms.every(term => normalizedText.includes(term));
}

/**
 * Filtra una lista de productos basado en un query de búsqueda.
 * Busca en nombre y marca del producto.
 * @param {Array} products - Lista de productos
 * @param {string} query - Query de búsqueda
 * @param {Object} options - Opciones adicionales
 * @param {number} options.limit - Límite de resultados (default: sin límite)
 * @param {Array<string>} options.searchFields - Campos donde buscar (default: ['name', 'brand'])
 * @returns {Array} - Productos filtrados
 */
export function filterProductsBySearch(products, query, options = {}) {
  if (!query || !query.trim()) return [];
  if (!products || !Array.isArray(products)) return [];
  
  const { limit = 0, searchFields = ['name', 'brand'] } = options;
  
  const filtered = products.filter(product => {
    // Combinar todos los campos de búsqueda en un solo texto
    const searchableText = searchFields
      .map(field => product[field] || '')
      .join(' ');
    
    return matchesSearch(searchableText, query);
  });
  
  return limit > 0 ? filtered.slice(0, limit) : filtered;
}

/**
 * Función debounce genérica para optimizar búsquedas.
 * @param {Function} func - Función a ejecutar
 * @param {number} wait - Tiempo de espera en ms
 * @returns {Function} - Función con debounce aplicado
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
} 